import { styled, alpha } from '@material-ui/core/styles'
import { Card, CardContent, Typography, Box, Tabs, ListItem } from '@material-ui/core'

// Main Dashboard Container
export const DashboardContainer = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
  '& .MuiContainer-root': {
    padding: '0 !important',
    maxWidth: 'none !important',
  },
  '& .MuiPaper-root': {
    boxShadow: 'none !important',
  },
}))

// Dashboard Content
export const DashboardContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'visible',
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}))

// Styled Card
export const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
}))

// Styled Card Content
export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minHeight: 0,
  overflow: 'hidden',
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}))

// Content Scroll Area with custom scrollbar
export const ContentScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(0, 0),
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.background.paper, 0.1),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[500], 0.45),
    borderRadius: '4px',
    border: 'none',
    '&:hover': {
      background: alpha(theme.palette.grey[500], 0.45),
    },
    '&:active': {
      background: alpha(theme.palette.grey[500], 0.7),
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: alpha(theme.palette.background.paper, 0.1),
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.grey[500], 0.45)} transparent`,
}))

// Scrollable List with custom scrollbar
export const ScrollableList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
  // Custom scrollbar styling for lists
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.background.paper, 0.05),
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[500], 0.45),
    borderRadius: '3px',
    border: 'none',
    '&:hover': {
      background: alpha(theme.palette.grey[500], 0.45),
    },
    '&:active': {
      background: alpha(theme.palette.grey[500], 0.7),
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: alpha(theme.palette.background.paper, 0.05),
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.grey[500], 0.45)} transparent`,
}))

// Dashboard Header
export const DashboardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0.5, 0),
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

// Dashboard Title
export const DashboardTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.25),
}))

// Dashboard Subtitle
export const DashboardSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  marginBottom: 0,
}))

// Styled Tabs
export const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: 'none',
  marginBottom: 0,
  minHeight: 'auto',
  '& .MuiTab-root': {
    minWidth: 'auto',
    padding: theme.spacing(1, 1.5),
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
    minHeight: '40px',
    borderRadius: theme.shape.borderRadius,
    marginRight: theme.spacing(0.25),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(0.25),
  },
}))

// Stat Card
export const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusSm,
  background: theme.palette.background.neutral,
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  margin: theme.spacing(0.25, 0),
}))

// Stat Label
export const StatLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.25),
}))

// Stat Value
export const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.375rem',
  fontWeight: 600,
  lineHeight: 1.2,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
  marginBottom: theme.spacing(0.25),
}))

// Progress Container
export const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}))

// Loading Wrapper
export const LoadingWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: alpha(theme.palette.background.paper, 0.8),
  zIndex: 1,
}))

// Recent Upload Item
export const RecentUploadItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))
